import React, { useEffect, useMemo } from 'react';
import { faCloud } from '@fortawesome/pro-regular-svg-icons';
import { FormRadio, NotificationDispatch, RadioGroup } from '@tryhackme/thm-ui-components';
import { RoomCloud, RoomCloudDetail } from 'src/features/manage-room/manage-room.types';
import { UpdateRoomConfirmModal } from 'src/features/manage-room/components/update-room-confirm-modal';
import { SettingCard } from 'src/features/manage-room/components/setting-card';
import { DiscardChangesModal } from 'src/features/manage-room/components/discard-changes-modal';
import { FormRow } from 'src/features/manage-room/components/form-row';
import { StyledUpdateRoomButtonContainer } from 'src/features/manage-room/components/categories-tab/categories-tab.styles';
import { Controller, useForm } from 'src/common/form';
import { useUpdateCloudIntegrationMutation } from 'src/features/manage-room/manage-room.slice';
import { StyledRadioGroup } from 'src/features/manage-room/components/general-settings/components/room-settings-section/room-settings-section.styles';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { displayErrors } from 'src/features/manage-room/helpers';
import { store } from 'src/app/store';
import { setIsDirty } from 'src/app/reducers/manage-room.reducer';
import { REGULAR_LAB, TENANT_LAB } from 'src/features/admin/cloud-training/azure-lab-config/azure-lab-config.constants';
type CloudFormData = {
  integration: 'none' | 'azure';
  azureMultiTenant: 'Regular Lab' | 'Tenant Lab';
};
export const CloudIntegrationTab = ({
  roomDetails
}: {
  roomDetails: RoomCloudDetail;
}) => {
  const [openUpdateModal, setOpenUpdateModal] = React.useState<boolean>(false);
  const [updateCloudIntegration, {
    isLoading
  }] = useUpdateCloudIntegrationMutation();
  const defaultValues = useMemo(() => ({
    integration: roomDetails.cloud?.azure?.isEnabled ? 'azure' : 'none',
    azureMultiTenant: roomDetails.cloud?.azure?.isMultiTenant ? TENANT_LAB : REGULAR_LAB
  } as CloudFormData), [roomDetails.cloud?.azure?.isEnabled, roomDetails.cloud?.azure?.isMultiTenant]);
  const formMethods = useForm<CloudFormData>({
    defaultValues,
    mode: 'onChange'
  });
  const {
    control,
    formState: {
      isDirty,
      errors
    },
    reset,
    handleSubmit
  } = formMethods;
  const handleDiscardChanges = () => {
    reset();
  };
  useEffect(() => {
    store.dispatch(setIsDirty(isDirty));
  }, [isDirty]);
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);
  const onSubmit = async (values: CloudFormData) => {
    try {
      const updatePayload: Partial<RoomCloud> = {
        azure: {
          isEnabled: values.integration === 'azure',
          isMultiTenant: values.azureMultiTenant === TENANT_LAB
        }
      };
      await updateCloudIntegration({
        roomCode: roomDetails.code,
        payload: updatePayload
      }).unwrap();
    } catch (error: unknown) {
      if (isQueryErrorWithMessage(error)) {
        displayErrors(error);
      } else {
        NotificationDispatch('error', 'Uh-oh! There was an error while updating cloud settings. Please try again.');
      }
    } finally {
      setOpenUpdateModal(false);
    }
  };
  return <section aria-label="cloud-integrations-tab" data-sentry-component="CloudIntegrationTab" data-sentry-source-file="cloud-integration-tab.tsx">
      <SettingCard icon={faCloud} title="Cloud Integration" data-sentry-element="SettingCard" data-sentry-source-file="cloud-integration-tab.tsx">
        <FormRow heading="Cloud room" description="Does this room support cloud training? Choosing 'Azure' will allow the selection of azure labs in the 'Tasks' section." data-sentry-element="FormRow" data-sentry-source-file="cloud-integration-tab.tsx">
          <Controller name="integration" control={control} render={({
          field: {
            value,
            onChange
          }
        }) => <RadioGroup legend="">
                <StyledRadioGroup>
                  <FormRadio checked={value === 'none'} onChange={onChange} id="none" label="None" value="none" name="cloud" />
                  <FormRadio checked={value === 'azure'} onChange={onChange} id="azure" label="Azure" value="azure" name="cloud" />
                </StyledRadioGroup>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="cloud-integration-tab.tsx" />
        </FormRow>
        <FormRow heading="Azure Lab Type" description="Controls the type of labs that can be connected to tasks of this room. Note that changing this setting will lead to any existing labs set on tasks of this room being unset." data-sentry-element="FormRow" data-sentry-source-file="cloud-integration-tab.tsx">
          <Controller name="azureMultiTenant" control={control} render={({
          field: {
            onChange,
            value
          }
        }) => <RadioGroup legend="">
                <StyledRadioGroup>
                  <FormRadio checked={value === REGULAR_LAB} onChange={onChange} id="regular" label={`${REGULAR_LAB}s`} value={REGULAR_LAB} name="type" />
                  <FormRadio checked={value === TENANT_LAB} onChange={onChange} id="tenant" label={`${TENANT_LAB}s`} value={TENANT_LAB} name="type" />
                </StyledRadioGroup>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="cloud-integration-tab.tsx" />
        </FormRow>
      </SettingCard>
      <StyledUpdateRoomButtonContainer data-sentry-element="StyledUpdateRoomButtonContainer" data-sentry-source-file="cloud-integration-tab.tsx">
        {isDirty && <DiscardChangesModal handleActionButton={handleDiscardChanges} />}
        <UpdateRoomConfirmModal open={openUpdateModal} openChange={setOpenUpdateModal} isRoomCodeChanged={false} isUpdatingRoom={isLoading} isUpdateButtonDisabled={!isDirty || Object.keys(errors).length > 0}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleUpdateRoom={handleSubmit(onSubmit)} data-sentry-element="UpdateRoomConfirmModal" data-sentry-source-file="cloud-integration-tab.tsx" />
      </StyledUpdateRoomButtonContainer>
    </section>;
};